import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Store, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { SignupForm } from "@/components/SignupForm";
import { Button } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { t } from "@/lib/i18n";
import { forgetShopClaim, rememberShopClaim } from "@/lib/shop-claim";
import { Shell } from "./Login";

const errorText = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? t("Das hat nicht geklappt – bitte erneut versuchen.")) : t("Verbindung fehlgeschlagen – bitte erneut versuchen."));

/**
 * Shop aus der Logiful-App in Shopify verbinden (öffentliche App):
 * - neues Konto: Selbst-Registrierung, startet im Testbetrieb, Logiful schaltet nach Vertragsabschluss frei
 * - bestehendes Konto: anmelden, Kunden wählen, Shop verknüpfen
 */
export function ShopifyConnectPage() {
  const [params] = useSearchParams();
  const claim = params.get("claim") ?? "";
  const [mode, setMode] = useState<"new" | "existing">(params.get("mode") === "existing" ? "existing" : "new");
  const info = useQuery({ queryKey: ["shop-claim", claim], queryFn: () => api.shopifyClaim.get(claim), retry: false, enabled: !!claim });

  if (!claim || info.isError)
    return (
      <Shell>
        <div className="size-12 rounded-2xl bg-danger-soft grid place-items-center"><TriangleAlert className="size-6 text-danger" /></div>
        <h1 className="mt-5 text-[24px] font-semibold tracking-tight">{t("Link nicht mehr gültig")}</h1>
        <p className="mt-2 text-fg-muted">{t("Öffne die Logiful-App in deinem Shopify-Admin erneut – dort bekommst du einen neuen Link.")}</p>
      </Shell>
    );
  if (!info.data) return <Shell><p className="text-fg-muted">{t("Wird geladen …")}</p></Shell>;
  const shop = info.data.shop;

  return (
    <Shell>
      <div className="size-12 rounded-2xl bg-coral/10 grid place-items-center"><Store className="size-6 text-coral" /></div>
      <h1 className="mt-5 text-[26px] font-semibold tracking-tight">{t("Shop mit Logiful verbinden")}</h1>
      <p className="mt-1.5 text-fg-muted">{t("Shop: {shop}", { shop })}</p>
      <div className="mt-6 grid grid-cols-2 gap-1 rounded-lg bg-surface-2 p-1 text-[13px] font-medium" role="tablist">
        {(["new", "existing"] as const).map((m) => (
          <button key={m} role="tab" aria-selected={mode === m} onClick={() => setMode(m)} className={`rounded-md px-3 py-1.5 ${mode === m ? "bg-surface shadow-sm text-fg" : "text-fg-muted hover:text-fg"}`}>
            {m === "new" ? t("Neues Konto") : t("Bereits Kunde")}
          </button>
        ))}
      </div>
      {mode === "new" ? <RegisterForm claim={claim} shop={shop} /> : <LinkExisting claim={claim} shop={shop} />}
    </Shell>
  );
}

function RegisterForm({ claim, shop }: { claim: string; shop: string }) {
  return (
    <SignupForm
      submit={async (d) => {
        await api.shopifyClaim.register({ claim, ...d });
        forgetShopClaim();
      }}
      submitLabel={t("Konto anlegen und {shop} verbinden", { shop: shop.replace(".myshopify.com", "") })}
    />
  );
}

function LinkExisting({ claim, shop }: { claim: string; shop: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false });
  const admins = (me.data?.tenants ?? []).filter((x) => x.role === "customer_admin" || x.role === "staff_admin" || x.role === "staff");
  const [tenantId, setTenantId] = useState("");
  useEffect(() => {
    if (!tenantId && admins.length === 1) setTenantId(admins[0]!.tenantId);
  }, [admins, tenantId]);
  const link = useMutation({
    mutationFn: () => api.shopifyClaim.link(claim, tenantId),
    onSuccess: async (r) => {
      forgetShopClaim();
      await qc.invalidateQueries({ queryKey: ["channels"] });
      navigate(`/channels/${r.channelId}`, { replace: true });
    },
  });

  if (me.isLoading) return <p className="mt-6 text-fg-muted">{t("Wird geladen …")}</p>;
  if (!me.data)
    return (
      <div className="mt-6">
        <p className="text-fg-muted text-[14px]">{t("Melde dich mit deinem Logiful-Konto an. Danach kommst du hierher zurück und verknüpfst den Shop.")}</p>
        <Link to="/login" onClick={() => rememberShopClaim(claim)}>
          <Button variant="primary" className="mt-5 w-full h-10">{t("Anmelden")} <ArrowRight className="size-4" /></Button>
        </Link>
      </div>
    );
  if (!admins.length) return <p className="mt-6 text-fg-muted text-[14px]">{t("Nur Administratoren können einen Shop verknüpfen. Bitte wende dich an den Administrator eures Logiful-Kontos.")}</p>;
  return (
    <div className="mt-6 space-y-3">
      {admins.length > 1 && (
        <label className="block">
          <span className="text-[12.5px] font-medium text-fg-muted">{t("Kunde")}</span>
          <select className="mt-1 w-full h-9 rounded-md border border-border bg-surface px-2 text-[14px]" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            <option value="">{t("Bitte wählen")}</option>
            {admins.map((a) => <option key={a.tenantId} value={a.tenantId}>{a.name}</option>)}
          </select>
        </label>
      )}
      {admins.length === 1 && <p className="text-[14px] text-fg-muted">{t("Der Shop wird mit {tenant} verknüpft.", { tenant: admins[0]!.name })}</p>}
      {link.isError && <p className="text-[13px] text-danger">{errorText(link.error)}</p>}
      <Button variant="primary" className="w-full h-10" disabled={!tenantId} loading={link.isPending} onClick={() => link.mutate()}>
        {t("{shop} verknüpfen", { shop: shop.replace(".myshopify.com", "") })} <ArrowRight className="size-4" />
      </Button>
    </div>
  );
}
